import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { config } from "config";

// Track whether a token refresh is in progress
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  isRefreshing = false;
  failedQueue = [];
};

// Create axios instance
const apiClient = axios.create({
  baseURL: config.apiBaseUrl as string,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.log("API Error intercepted:", {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
    });

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("401 error detected, attempting token refresh...");

      if (isRefreshing) {
        // Queue the request if a refresh is already in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        console.log("Calling refresh token endpoint...");
        const response = await axios.post(
          `${config.apiBaseUrl}/auth/refresh`,
          {},
          {
            withCredentials: true,
          }
        );

        const { token } = response.data;
        console.log("Token refresh successful");

        // Store the new token (assumes localStorage or cookie storage)
        if (typeof window !== "undefined") {
          localStorage.setItem("accessToken", token);
        }

        // Update the authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        // Process queued requests with the new token
        processQueue(null, token);

        // Retry the original request with the new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, redirect to login
        console.error("Token refresh failed:", refreshError);
        processQueue(refreshError as AxiosError, null);

        if (typeof window !== "undefined") {
          console.log("Redirecting to login...");
          const currentHost = window.location.origin;
          const redirectUrl = `${currentHost}/dashboard`;
          const loginUrl = `${config.apiBaseUrl}/auth/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
          window.location.href = loginUrl;
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
